import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/config";
import { PlayCircle, RotateCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";

const SKIP_FOREVER_KEY = "fp_intro_skip_forever";

export default function Trailer() {
  const [meta, setMeta] = useState(null);
  const [skipForever, setSkipForever] = useState(false);

  useEffect(() => {
    api.get("/intro-video/meta")
      .then((r) => setMeta(r.data))
      .catch(() => setMeta({ available: false }));
    try { setSkipForever(localStorage.getItem(SKIP_FOREVER_KEY) === "1"); } catch { /* noop */ }
  }, []);

  if (!meta || !meta.available) return null;

  const streamUrl = `${api.defaults.baseURL}/intro-video/stream?v=${encodeURIComponent(meta.version || meta.uploaded_at || "")}`;

  const enableIntroAgain = () => {
    try {
      localStorage.removeItem(SKIP_FOREVER_KEY);
      sessionStorage.removeItem("fp_intro_skip_session");
      setSkipForever(false);
      toast.success("Video introduttivo riattivato! Ricarica la pagina per rivederlo.");
    } catch {
      toast.error("Impossibile riattivare (storage bloccato dal browser)");
    }
  };

  return (
    <section id="trailer" className="relative py-24 md:py-32 px-5 md:px-8" data-testid="trailer-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 text-xs tracking-[0.4em] uppercase text-fp-cyan font-heading font-semibold">
            <Sparkles size={14} /> Il nostro trailer
          </div>
          <h2 className="mt-4 font-heading font-black text-4xl md:text-5xl lg:text-6xl tracking-tight silver-gradient">
            Guarda il <span className="cyan-gradient">Trailer</span>
          </h2>
          <p className="mt-4 text-white/60 text-sm md:text-base max-w-2xl mx-auto">
            Un assaggio dell'edizione 2026/2027 di Fanta Partenope.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative rounded-3xl overflow-hidden border border-fp-cyan/30 shadow-[0_0_60px_-10px_rgba(0,229,255,0.4)] bg-black"
        >
          <video
            src={streamUrl}
            className="w-full aspect-video bg-black"
            controls
            controlsList="nodownload"
            preload="metadata"
            playsInline
            poster=""
            data-testid="trailer-video"
          />
          <div className="pointer-events-none absolute inset-0 rounded-3xl ring-1 ring-inset ring-white/10" />
        </motion.div>

        {skipForever && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 p-5 rounded-2xl border border-fp-cyan/30 bg-fp-cyan/5 flex flex-col sm:flex-row sm:items-center gap-4"
            data-testid="trailer-restore-block"
          >
            <PlayCircle className="text-fp-cyan flex-shrink-0" size={22} />
            <div className="flex-1">
              <div className="font-heading font-bold text-white">Video introduttivo disattivato</div>
              <div className="text-sm text-white/60 mt-1">
                Hai scelto "Salta sempre" in un accesso precedente. Puoi riattivare la riproduzione automatica all'ingresso nel sito.
              </div>
            </div>
            <button
              onClick={enableIntroAgain}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-fp-cyan text-fp-bg font-heading font-bold text-sm uppercase tracking-wider hover:shadow-[0_0_20px_rgba(0,229,255,0.4)] transition-all"
              data-testid="trailer-restore-btn"
            >
              <RotateCcw size={16} /> Riattiva
            </button>
          </motion.div>
        )}
      </div>
    </section>
  );
}
